import { ReactElement } from 'react'
import { Formik, FormikErrors, getIn } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { getUnprocessableErrors } from 'utils/error'
import { Checkbox } from 'components/v2/Checkbox'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { INewPassengerPropertyForm, INewPassengerPropertyFormProps, INewPassengerPropertyProps } from './types'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { SelectCountries, useCountry } from 'components/SelectCountries'
import { GTCreatePassengerPropertyDateRangeInput, GTLanguage, GTPassengerTypeV2 } from 'gql/types.generated'
import { schema } from './schema'
import { SelectPassengerProperty } from '../SelectPassengerProperty'
import { InputDateTime } from 'components/InputDateTime'
import { isBefore, endOfDay, startOfDay } from 'date-fns'
import styled from 'styled-components'
import Heading from 'components/v2/Heading'
import { CloseIcon } from '@10xLabs/react-web/components/Icons/Close'
import { Label } from 'features/Bookings/components/Service'
import { TCountryCode } from 'types/index'
import { InputPercentage } from 'components/InputPercentage'
import SelectPassengerTypes from 'components/SelectPassengerTypes'
import { EPassengerProperty } from '../SelectPassengerProperty/types'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledLabelWithMargin = styled(Label)`
  font-weight: unset;
  margin-bottom: 5px;
`
const StyledLabel = styled(Label)`
  font-weight: unset;
`
const SpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const StyledRow = styled(Row)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-columns: 10px;
  gap: 20px;
  margin-block-end: 20px;
  padding: 0 10px;
`
const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
`
const IntervalDelete = styled.div`
  place-self: center;
  grid-column-start: 3;
`

const messages = defineMessages({
  passengerPropertyPlaceholder: {
    id: 'passengerProperty.new.form.name.placeholder',
    defaultMessage: 'Passenger Type'
  },
  enLabelPlaceholder: {
    id: 'passengerProperty.new.form.enLabel.placeholder',
    defaultMessage: 'English translation'
  },
  esLabelPlaceholder: {
    id: 'passengerProperty.new.form.esLabel.placeholder',
    defaultMessage: 'Spanish translation'
  },
  enAbbreviationPlaceholder: {
    id: 'passengerProperty.new.form.enAbbreviation.placeholder',
    defaultMessage: 'English translation'
  },
  esAbbreviationPlaceholder: {
    id: 'passengerProperty.new.form.esAbbreviation.placeholder',
    defaultMessage: 'Spanish translation'
  },
  enDescriptionPlaceholder: {
    id: 'passengerProperty.new.form.enDescription.placeholder',
    defaultMessage: 'English translation'
  },
  esDescriptionPlaceholder: {
    id: 'passengerProperty.new.form.esDescription.placeholder',
    defaultMessage: 'Spanish translation'
  },
  salesChannelsPlaceholder: {
    id: 'passengerProperty.new.form.salesChannels.placeholder',
    defaultMessage: 'Select Sales Channels'
  },
  selectCountryCode: {
    id: 'passengerProperty.new.form.selectCountryCode',
    defaultMessage: 'Select Country Code'
  },
  startAtPlaceholder: {
    id: 'passengerProperty.new.form.startAt.placeholder',
    defaultMessage: 'Start At'
  },
  endAtPlaceholder: {
    id: 'passengerProperty.new.form.endAt.placeholder',
    defaultMessage: 'End At'
  }
})

const NewPassengerPropertyForm = (props: INewPassengerPropertyFormProps): ReactElement => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    error,
    loading,
    isEdit,
    dirty
  } = props

  const intl = useIntl()
  const country = useCountry(values.countryCode)

  const addInterval = (key: 'departureDateRanges' | 'searchDateRanges'): void => {
    setFieldValue(key, [
      ...values[key],
      {
        startAt: '',
        endAt: ''
      }
    ])
  }
  const deleteInterval = (key: 'departureDateRanges' | 'searchDateRanges', index: number): void => {
    setFieldValue(
      key,
      values[key].filter((_, intervalIndex) => intervalIndex !== index)
    )
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            <SelectCountries
              value={values.countryCode}
              onChange={(value): void => {
                setFieldValue('countryCode', value)
              }}
              onBlur={handleBlur}
              touched={touched.countryCode}
              errors={errors.countryCode}
              disabled={isEdit}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectPassengerProperty
              onChange={(value: string): void => {
                setFieldValue('name', value)
              }}
              onBlur={handleBlur}
              value={values.name}
              touched={touched.name}
              errors={errors.name}
              disabled={isEdit}
              countryCode={values.countryCode as TCountryCode}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enLabel}
              placeholder={intl.formatMessage(messages.enLabelPlaceholder)}
              touched={touched.enLabel}
              errors={errors.enLabel}
              label={<FormattedMessage id="passengerProperty.new.form.enLabel.label" defaultMessage="English Label" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="esLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esLabel}
              placeholder={intl.formatMessage(messages.esLabelPlaceholder)}
              touched={touched.esLabel}
              errors={errors.esLabel}
              label={<FormattedMessage id="passengerProperty.new.form.esLabel.label" defaultMessage="Spanish Label" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enAbbreviation"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enAbbreviation}
              placeholder={intl.formatMessage(messages.enAbbreviationPlaceholder)}
              touched={touched.enAbbreviation}
              errors={errors.enAbbreviation}
              label={
                <FormattedMessage
                  id="passengerProperty.new.form.enAbbreviation.label"
                  defaultMessage="English Abbreviation"
                />
              }
            />
          </Column>
          <Column width="50%">
            <Input
              id="esAbbreviation"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esAbbreviation}
              placeholder={intl.formatMessage(messages.esAbbreviationPlaceholder)}
              touched={touched.esAbbreviation}
              errors={errors.esAbbreviation}
              label={
                <FormattedMessage
                  id="passengerProperty.new.form.esAbbreviation.label"
                  defaultMessage="Spanish Abbreviation"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enDescription"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enDescription}
              placeholder={intl.formatMessage(messages.enDescriptionPlaceholder)}
              touched={touched.enDescription}
              errors={errors.enDescription}
              label={
                <FormattedMessage
                  id="passengerProperty.new.form.enDescription.label"
                  defaultMessage="English Description"
                />
              }
            />
          </Column>
          <Column width="50%">
            <Input
              id="esDescription"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esDescription}
              placeholder={intl.formatMessage(messages.esDescriptionPlaceholder)}
              touched={touched.esDescription}
              errors={errors.esDescription}
              label={
                <FormattedMessage
                  id="passengerProperty.new.form.esDescription.label"
                  defaultMessage="Spanish Description"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <SpaceBetween>
              <Heading.H5>
                <FormattedMessage
                  id="passengerProperty.form.departureDatesRanges.label"
                  defaultMessage="Departure Dates"
                />
              </Heading.H5>
              <Button kind="secondary" type="button" onClick={(): void => addInterval('departureDateRanges')}>
                <FormattedMessage id="dictionary.add" defaultMessage="Add" />
              </Button>
            </SpaceBetween>
          </Column>
        </Row>
        {values.departureDateRanges.map(({ startAt, endAt }, index) => {
          return (
            <>
              <StyledLabelWithMargin>
                <FormattedMessage
                  id="fareRules.form.interval.label"
                  defaultMessage="Interval {count}"
                  values={{
                    count: index + 1
                  }}
                />
              </StyledLabelWithMargin>
              <StyledRow key={index}>
                <div>
                  <InputDateTime
                    id={`departureDateRanges[${index}].startAt`}
                    intl={intl}
                    value={startAt}
                    onChange={(value?: Date): void => {
                      if (!value) return

                      setFieldValue(`departureDateRanges[${index}].startAt`, value)

                      if (!endAt || isBefore(endAt, value)) {
                        setFieldValue(`departureDateRanges[${index}].endAt`, endOfDay(value))
                      }
                    }}
                    disabledDays={{
                      before: new Date()
                    }}
                    touched={Boolean(touched?.departureDateRanges && touched.departureDateRanges[index]?.startAt)}
                    errors={
                      errors?.departureDateRanges &&
                      errors.departureDateRanges[index] &&
                      ((errors.departureDateRanges as FormikErrors<GTCreatePassengerPropertyDateRangeInput>[])[index]
                        .startAt as string)
                    }
                    placeholder={intl.formatMessage(messages.startAtPlaceholder)}
                    label=""
                  />
                </div>
                <div>
                  <InputDateTime
                    id={`departureDateRanges[${index}].endAt`}
                    intl={intl}
                    value={endAt}
                    onChange={(value?: Date): void => {
                      if (!value) return

                      setFieldValue(`departureDateRanges[${index}].endAt`, value)

                      if (!startAt || isBefore(value, startAt)) {
                        setFieldValue(`departureDateRanges[${index}].startAt`, startOfDay(value))
                      }
                    }}
                    disabledDays={{
                      before: new Date()
                    }}
                    touched={Boolean(touched?.departureDateRanges && touched.departureDateRanges[index]?.endAt)}
                    errors={
                      errors?.departureDateRanges &&
                      errors?.departureDateRanges[index] &&
                      ((errors.departureDateRanges as FormikErrors<GTCreatePassengerPropertyDateRangeInput>[])[index]
                        .endAt as string)
                    }
                    placeholder={intl.formatMessage(messages.endAtPlaceholder)}
                    label=""
                  />
                </div>
                <IntervalDelete>
                  <div onClick={(): void => deleteInterval('departureDateRanges', index)}>
                    <StyledCloseIcon />
                  </div>
                </IntervalDelete>
              </StyledRow>
            </>
          )
        })}
        <Row>
          <Column>
            <SpaceBetween>
              <Heading.H5>
                <FormattedMessage id="passengerProperty.form.searchDateRanges.label" defaultMessage="Search Dates" />
              </Heading.H5>
              <Button kind="secondary" type="button" onClick={(): void => addInterval('searchDateRanges')}>
                <FormattedMessage id="dictionary.add" defaultMessage="Add" />
              </Button>
            </SpaceBetween>
          </Column>
        </Row>
        {values.searchDateRanges.map(({ startAt, endAt }, index) => {
          return (
            <>
              <StyledLabelWithMargin>
                <FormattedMessage
                  id="fareRules.form.interval.label"
                  defaultMessage="Interval {count}"
                  values={{
                    count: index + 1
                  }}
                />
              </StyledLabelWithMargin>
              <StyledRow key={index}>
                <div>
                  <InputDateTime
                    id={`searchDateRanges[${index}].startAt`}
                    intl={intl}
                    value={startAt}
                    onChange={(value?: Date): void => {
                      if (!value) return

                      setFieldValue(`searchDateRanges[${index}].startAt`, value)

                      if (!endAt || isBefore(endAt, value)) {
                        setFieldValue(`searchDateRanges[${index}].endAt`, endOfDay(value))
                      }
                    }}
                    disabledDays={{
                      before: new Date()
                    }}
                    touched={Boolean(touched?.searchDateRanges && touched.searchDateRanges[index]?.startAt)}
                    errors={
                      errors?.searchDateRanges &&
                      errors?.searchDateRanges[index] &&
                      ((errors.searchDateRanges as FormikErrors<GTCreatePassengerPropertyDateRangeInput>[])[index]
                        .startAt as string)
                    }
                    placeholder={intl.formatMessage(messages.startAtPlaceholder)}
                    label=""
                  />
                </div>
                <div>
                  <InputDateTime
                    id={`searchDateRanges[${index}].endAt`}
                    intl={intl}
                    value={endAt}
                    onChange={(value?: Date): void => {
                      if (!value) return

                      setFieldValue(`searchDateRanges[${index}].endAt`, value)

                      if (!startAt || isBefore(value, startAt)) {
                        setFieldValue(`searchDateRanges[${index}].startAt`, startOfDay(value))
                      }
                    }}
                    disabledDays={{
                      before: new Date()
                    }}
                    touched={Boolean(touched?.searchDateRanges && touched.searchDateRanges[index]?.endAt)}
                    errors={
                      errors?.searchDateRanges &&
                      errors?.searchDateRanges[index] &&
                      ((errors.searchDateRanges as FormikErrors<GTCreatePassengerPropertyDateRangeInput>[])[index]
                        .endAt as string)
                    }
                    placeholder={intl.formatMessage(messages.endAtPlaceholder)}
                    label=""
                  />
                </div>
                <IntervalDelete>
                  <div onClick={(): void => deleteInterval('searchDateRanges', index)}>
                    <StyledCloseIcon />
                  </div>
                </IntervalDelete>
              </StyledRow>
            </>
          )
        })}
        <Row>
          <Column>
            <StyledLabel>
              <FormattedMessage id="dictionary.passengerType" defaultMessage="Passenger Type" />
            </StyledLabel>
            <SelectPassengerTypes
              id="passengerTypes"
              countryCode={values.countryCode}
              onChange={(value): void => {
                setFieldValue('passengerTypes', value)
                setFieldValue(
                  'settings.passengerTypeIDs',
                  value.map((item) => item.id)
                )
              }}
              values={values.passengerTypes ?? []}
              touched={touched.settings?.passengerTypeIDs}
              errors={errors.settings?.passengerTypeIDs}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <div>
              <FormattedMessage id="passengerProperty.form.salesChannel.label" defaultMessage="Sales Channel" />
            </div>
            <SelectSalesChannelsV2
              id="settings.salesChannels"
              countryId={country?.id}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              values={values.settings.salesChannels ?? []}
              onChange={(salesChannels: ISalesChannel[]): void => {
                setFieldValue('settings.salesChannels', salesChannels)
              }}
              touched={getIn(touched, 'settings.salesChannels')}
              errors={getIn(errors, 'settings.salesChannels')}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <StyledLabelWithMargin>
              <FormattedMessage id="passengerProperty.form.discount" defaultMessage="Discount" />
            </StyledLabelWithMargin>
            <InputPercentage
              disabled={false}
              value={Number(values.settings.discount) >= 0 ? String(values.settings.discount) : ''}
              placeholder="0%"
              onChange={(value: INewPassengerPropertyForm['settings']['discount']): void => {
                setFieldValue('settings.discount', value)
              }}
              onBlur={handleBlur}
              touched={touched.settings?.discount}
              errors={errors.settings?.discount}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <CheckboxContainer>
              <Checkbox
                id="passengerProperty.form.propertyRequired"
                checked={values.required}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  setFieldValue('required', event.target.checked)
                }}
              />
              <FormattedMessage id="passengerProperty.form.propertyRequired.label" defaultMessage="Required" />
            </CheckboxContainer>
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="passengerProperty.form.error"
                defaultMessage="There was an error while saving your passenger property."
              />
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/passenger-properties" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
            {props.isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewPassengerProperty = ({
  onSubmit,
  error,
  loading,
  isEdit,
  passengerPropertyData
}: INewPassengerPropertyProps): ReactElement => {
  const intl = useIntl()
  const getInitialValues = (): INewPassengerPropertyForm => {
    if (isEdit && passengerPropertyData) {
      return {
        passengerTypes: passengerPropertyData.settings[0].passengerTypes as GTPassengerTypeV2[],
        countryCode: passengerPropertyData.countryCode as TCountryCode,
        name: passengerPropertyData.name as EPassengerProperty,
        enLabel: passengerPropertyData.label?.find((label) => label.language === GTLanguage.En)?.text ?? '',
        esLabel: passengerPropertyData.label?.find((label) => label.language === GTLanguage.Es)?.text ?? '',
        enAbbreviation:
          passengerPropertyData.abbreviation?.find((abbreviation) => abbreviation.language === GTLanguage.En)?.text ??
          '',
        esAbbreviation:
          passengerPropertyData.abbreviation?.find((abbreviation) => abbreviation.language === GTLanguage.Es)?.text ??
          '',
        enDescription:
          passengerPropertyData.description?.find((description) => description.language === GTLanguage.En)?.text ?? '',
        esDescription:
          passengerPropertyData.description?.find((description) => description.language === GTLanguage.Es)?.text ?? '',
        required: passengerPropertyData.required,
        settings: {
          discount: passengerPropertyData.settings[0].discount,
          passengerTypeIDs: passengerPropertyData.settings[0].passengerTypes.map(
            (passengerType) => passengerType?.id ?? ''
          ),
          salesChannels:
            passengerPropertyData.settings[0].salesChannelsV2?.map((saleChannelV2) => {
              return {
                id: saleChannelV2.id,
                name: saleChannelV2.name
              }
            }) ?? []
        },
        searchDateRanges: passengerPropertyData.searchDateRanges.map((searchDateRange) => ({
          startAt: new Date(searchDateRange.startAt),
          endAt: new Date(searchDateRange.endAt)
        })),
        departureDateRanges: passengerPropertyData.departureDateRanges.map((departureDateRange) => ({
          startAt: new Date(departureDateRange.startAt),
          endAt: new Date(departureDateRange.endAt)
        }))
      }
    }
    return {
      passengerTypes: [],
      name: '',
      countryCode: '',
      enLabel: '',
      esLabel: '',
      enAbbreviation: '',
      esAbbreviation: '',
      enDescription: '',
      esDescription: '',
      departureDateRanges: [],
      searchDateRanges: [],
      required: false,
      settings: {
        discount: 0,
        passengerTypeIDs: [],
        salesChannels: []
      }
    }
  }
  const initialValues = getInitialValues()
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema(intl)}>
      {(props): ReactElement => {
        return <NewPassengerPropertyForm {...props} error={error} loading={loading} isEdit={isEdit} />
      }}
    </Formik>
  )
}
